/**
 * Keep navigation in the hosts: signup follows a fixed sequence, while profile setup allows any order. This
 * composable shares run state and API operations.
 */

import { useFetch } from "~vue/api";
import type {
    AccessLevel,
    AssessmentState,
    CalendarScanStats,
    OnboardingStep,
    PerspectiveRevealState,
    ProfilePreview,
    ReadingsState,
    RunState,
    ScanState,
} from "~vue/CalendarFirstOnboarding/types";
import { type PollingPhase, usePollingPhase } from "~vue/CalendarFirstOnboarding/usePollingPhase";
import { i18n } from "~vue/i18n";
import type { PerspectiveResult } from "~vue/pages/perspectiveTypes";
import { computed, type ComputedRef, type Ref, ref, watch } from "vue";

const BASE = "/onboarding/calendar-first";

export interface IdentitySubmission {
    firstName: string;
    lastName: string;
    role: string;
    managesPeople: boolean;
}

/**
 * Return empty-step outcomes for hosts to handle navigation. Without a profile, the linear flow ends the
 * run; guided setup advances to the next question.
 */
type EnterStepOutcome = "entered" | "nothing_to_reveal" | "nothing_to_publish";

/** Annotated rather than inferred: the inferred shape is not nameable across package boundaries. */
interface OnboardingRun {
    run: Ref<RunState>;
    busy: Ref<boolean>;
    apiError: Ref<boolean>;
    assessment: Ref<AssessmentState>;
    profilePreview: Ref<ProfilePreview>;
    setProfileSubtopicContent: (id: string, content: string) => void;

    scanPhase: PollingPhase<ScanState>;
    readingsPhase: PollingPhase<ScanState>;
    sharpenedPhase: PollingPhase<ReadingsState>;
    profilePhase: PollingPhase<ProfilePreview>;

    managesPeople: ComputedRef<boolean>;
    firstName: ComputedRef<string>;
    perspectiveResult: ComputedRef<PerspectiveResult | null>;
    scanStats: ComputedRef<CalendarScanStats | null>;
    scanReadings: ComputedRef<ReadingsState | null>;
    sharpenedReadings: ComputedRef<ReadingsState | null>;
    readingsSettled: ComputedRef<boolean>;
    sharpenedSettled: ComputedRef<boolean>;
    hasSharpenedReadings: ComputedRef<boolean>;
    profileGenerating: ComputedRef<boolean>;
    /** Nothing was generated and nothing will be, so there is no document to publish. */
    profileSkipped: ComputedRef<boolean>;
    scanLabel: ComputedRef<string>;

    enterStep: (step: OnboardingStep) => Promise<EnterStepOutcome>;
    stopAllPhases: () => void;

    submitIdentity: (payload: IdentitySubmission) => Promise<boolean>;
    connectCalendar: (timezone: string) => Promise<void>;
    skipCalendar: () => Promise<boolean>;
    startScan: () => Promise<boolean>;
    cancelScanStart: () => void;
    completeAssessment: () => Promise<boolean>;
    skipAssessment: () => Promise<boolean>;
    publishProfile: (access: AccessLevel) => Promise<boolean>;
    advance: (completedStep: OnboardingStep) => Promise<boolean>;
    complete: () => Promise<string | null>;
    skipOnboarding: () => Promise<string | null>;
}

function isReadingsSettled(readings: ReadingsState | null | undefined): boolean {
    return readings !== null && readings !== undefined && readings.status !== "not_started" && readings.status !== "running";
}

interface OnboardingRunOptions {
    /**
     * The server uses this entry point to choose the calendar OAuth return URL; omitting it sends profile
     * setup users back to the linear flow.
     */
    entry?: string;
}

export function useOnboardingRun(initialRun: RunState, { entry }: OnboardingRunOptions = {}): OnboardingRun {
    const entryHeaders: Record<string, string> = entry === undefined ? {} : { "X-Setup-Entry": entry };
    const run = ref<RunState>(initialRun);
    const busy = ref(false);
    const apiError = ref(false);

    const assessment = ref<AssessmentState>({ session_id: "", questions: [], saved_responses: {} });
    const profilePreview = ref<ProfilePreview>({ status: "not_started", name: "", role: "", subtopics: [], had_existing_content: false });
    /* The step's loading screen uses null to mean the archetype has not been fetched. */
    const perspectiveReveal = ref<PerspectiveRevealState | null>(null);

    const managesPeople = computed(() => run.value.manages_people === true);
    /* Empty when the directory had no name; IntroStep greets without one rather than inventing a placeholder. */
    const firstName = computed(() => run.value.identity_prefill.first_name);
    const perspectiveResult = computed(() => perspectiveReveal.value?.result ?? null);

    const scanPhase = usePollingPhase<ScanState>(`${BASE}/scan/`, {
        isResolved: (payload) => payload.status === "stats_ready",
        isFailed: (payload) => payload.status === "failed",
    });
    const readingsPhase = usePollingPhase<ScanState>(`${BASE}/scan/`, {
        isResolved: (payload) => isReadingsSettled(payload.readings),
    });
    const sharpenedPhase = usePollingPhase<ReadingsState>(`${BASE}/sharpened/`, {
        isResolved: isReadingsSettled,
    });
    const profilePhase = usePollingPhase<ProfilePreview>(`${BASE}/profile/`, {
        isResolved: (payload) => payload.status === "ready" || payload.status === "failed" || payload.status === "skipped",
    });

    /*
     * The previous step confirms the result was written before navigation, so fetch it once without
     * polling.
     */
    const {
        execute: executePerspectiveFetch,
        data: perspectiveData,
        error: perspectiveError,
    } = useFetch<PerspectiveRevealState>(`${BASE}/perspective/`, { immediate: false }).get().json<PerspectiveRevealState>();

    const scanStats = computed(() => readingsPhase.data.value?.stats ?? scanPhase.data.value?.stats ?? run.value.calendar_stats);
    const scanReadings = computed(() => readingsPhase.data.value?.readings ?? scanPhase.data.value?.readings ?? null);
    const sharpenedReadings = computed(() => sharpenedPhase.data.value);

    const readingsSettled = computed(() => isReadingsSettled(scanReadings.value) || readingsPhase.outcome.value === "timed_out" || readingsPhase.outcome.value === "error");
    const sharpenedSettled = computed(() => isReadingsSettled(sharpenedReadings.value) || sharpenedPhase.outcome.value === "timed_out" || sharpenedPhase.outcome.value === "error");
    const hasSharpenedReadings = computed(() => (sharpenedReadings.value?.readings.length ?? 0) > 0);
    const profileGenerating = computed(() => profilePreview.value.status === "running" || profilePreview.value.status === "not_started");
    /*
     * Skipping questions updates the run before any profile endpoint is called, so the preview alone cannot
     * identify skipped profiles.
     */
    const profileSkipped = computed(() => profilePreview.value.status === "skipped" || run.value.profile_status === "skipped");

    const scanLabel = computed(() => {
        const { t } = i18n.global;
        if (scanPhase.outcome.value === "timed_out" || scanPhase.outcome.value === "error") {
            return t("onboarding.calendarFirst.run.scanStalled");
        }
        const days = run.value.calendar_stats?.window_days ?? 30;
        return t("onboarding.calendarFirst.run.scanLabel", { days }, days);
    });

    interface PendingPost {
        path: string;
        abort: () => void;
    }
    let pendingPost: PendingPost | null = null;

    async function post<T>(path: string, body: unknown = {}): Promise<T | null> {
        if (busy.value) {
            return null;
        }
        const { execute, abort, data, error, response } = useFetch(`${BASE}${path}`, { headers: entryHeaders }, { immediate: false }).post(body).json<T>();
        const request: PendingPost = { path, abort };
        pendingPost = request;
        busy.value = true;
        try {
            await execute();
            if (pendingPost !== request) {
                return null;
            }
            if (error.value) {
                if (response.value?.status === 409) {
                    globalThis.location.href = globalThis.location.pathname;
                    return null;
                }
                apiError.value = true;
                return null;
            }
            return data.value;
        } finally {
            if (pendingPost === request) {
                pendingPost = null;
                busy.value = false;
            }
        }
    }

    function cancelScanStart(): void {
        if (pendingPost?.path !== "/scan/start/") {
            return;
        }
        const request = pendingPost;
        pendingPost = null;
        busy.value = false;
        request.abort();
    }

    /**
     * Callers must stop navigation when this returns false; advancing after a failed mutation would
     * desynchronize the displayed step from the server cursor.
     */
    function applyRunState(next: RunState | null): boolean {
        if (next === null) {
            return false;
        }
        run.value = next;
        return true;
    }

    async function submitIdentity(payload: IdentitySubmission): Promise<boolean> {
        return applyRunState(
            await post<RunState>("/identity/", {
                first_name: payload.firstName,
                last_name: payload.lastName,
                role: payload.role,
                manages_people: payload.managesPeople,
            }),
        );
    }

    async function setTimezone(timezone: string): Promise<boolean> {
        return applyRunState(await post<RunState>("/calendar/timezone/", { timezone }));
    }

    async function connectCalendar(timezone: string): Promise<void> {
        /*
         * Save the timezone successfully before navigating away. The provider callback returns here, where
         * the server derives connection state from `has_integrated_calendar`.
         */
        if (!(await setTimezone(timezone))) {
            return;
        }

        busy.value = true;
        try {
            const { data, error } = await useFetch<{ auth_url: string }>(run.value.calendar.auth_url_endpoint).get().json<{ auth_url: string }>();
            const authUrl = error.value ? null : data.value?.auth_url;
            if (!authUrl) {
                apiError.value = true;
                return;
            }
            globalThis.location.href = authUrl;
        } finally {
            busy.value = false;
        }
    }

    async function skipCalendar(): Promise<boolean> {
        return applyRunState(await post<RunState>("/calendar/skip/"));
    }

    /** Idempotent: adopts a live scan, re-enqueues one a dead worker stranded. */
    async function startScan(): Promise<boolean> {
        return applyRunState(await post<RunState>("/scan/start/"));
    }

    async function startAssessment(): Promise<boolean> {
        const state = await post<AssessmentState>("/assessment/start/");
        if (state === null) {
            return false;
        }
        assessment.value = state;
        return true;
    }

    async function completeAssessment(): Promise<boolean> {
        return applyRunState(await post<RunState>("/assessment/complete/"));
    }

    async function skipAssessment(): Promise<boolean> {
        return applyRunState(await post<RunState>("/assessment/skip/"));
    }

    async function generateProfile(): Promise<boolean> {
        const preview = await post<ProfilePreview>("/profile/generate/");
        if (preview === null) {
            return false;
        }
        profilePreview.value = preview;
        return true;
    }

    function setProfileSubtopicContent(id: string, content: string): void {
        const subtopic = profilePreview.value.subtopics.find((item) => item.id === id);
        if (subtopic) {
            subtopic.content = content;
        }
    }

    async function publishProfile(access: AccessLevel): Promise<boolean> {
        return applyRunState(await post<RunState>("/profile/publish/", { access }));
    }

    /** Advances the server's progress cursor without changing the displayed step. */
    async function advance(completedStep: OnboardingStep): Promise<boolean> {
        return applyRunState(await post<RunState>("/advance/", { completed_step: completedStep }));
    }

    async function leaveFlow(path: "/complete/" | "/skip/"): Promise<string | null> {
        const result = await post<{ redirect_url: string }>(path);
        return result === null ? null : result.redirect_url;
    }

    async function complete(): Promise<string | null> {
        return leaveFlow("/complete/");
    }

    async function skipOnboarding(): Promise<string | null> {
        return leaveFlow("/skip/");
    }

    function stopAllPhases(): void {
        scanPhase.stop();
        readingsPhase.stop();
        sharpenedPhase.stop();
        profilePhase.stop();
    }

    /**
     * Invalidates earlier entries so responses arriving after navigation cannot overwrite the current
     * step's payload.
     */
    let entryToken = 0;

    async function enterStep(step: OnboardingStep): Promise<EnterStepOutcome> {
        const token = ++entryToken;
        const abandoned = (): boolean => {
            if (token === entryToken) {
                return false;
            }
            apiError.value = false;
            return true;
        };

        stopAllPhases();

        if (step === "scanning") {
            const started = await startScan();
            if (abandoned()) {
                return "entered";
            }
            if (started) {
                scanPhase.start();
            }
            return "entered";
        }
        if (step === "calendar_payoff") {
            readingsPhase.start();
            return "entered";
        }
        if (step === "assessment") {
            await startAssessment();
            return "entered";
        }
        if (step === "perspective_result") {
            perspectiveReveal.value = null;
            await executePerspectiveFetch();
            if (abandoned()) {
                return "entered";
            }
            const reveal = perspectiveError.value ? null : perspectiveData.value;
            /*
             * Missing or failed perspective reads skip the reveal without setting apiError because readings
             * are still being generated.
             */
            if (reveal === null || reveal.result === null) {
                return "nothing_to_reveal";
            }
            perspectiveReveal.value = reveal;
            return "entered";
        }
        if (step === "sharpened") {
            sharpenedPhase.start();
            return "entered";
        }
        if (step === "profile") {
            /* Skipping the questions leaves the profile generator without source data. */
            if (profileSkipped.value) {
                return "nothing_to_publish";
            }
            await generateProfile();
            if (abandoned()) {
                return "entered";
            }
            if (profileSkipped.value) {
                return "nothing_to_publish";
            }
            profilePhase.start();
        }
        return "entered";
    }

    watch(
        () => profilePhase.data.value,
        (preview) => {
            if (preview !== null) {
                profilePreview.value = preview;
            }
        },
    );

    return {
        run,
        busy,
        apiError,
        assessment,
        profilePreview,
        setProfileSubtopicContent,
        scanPhase,
        readingsPhase,
        sharpenedPhase,
        profilePhase,
        managesPeople,
        firstName,
        perspectiveResult,
        scanStats,
        scanReadings,
        sharpenedReadings,
        readingsSettled,
        sharpenedSettled,
        hasSharpenedReadings,
        profileGenerating,
        profileSkipped,
        scanLabel,
        enterStep,
        stopAllPhases,
        submitIdentity,
        connectCalendar,
        skipCalendar,
        startScan,
        cancelScanStart,
        completeAssessment,
        skipAssessment,
        publishProfile,
        advance,
        complete,
        skipOnboarding,
    };
}
